<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.customer', 2) },
      { label: $t('ui.reports.appointedCustomers'), to: '/reports/customers/appointed-customers' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-2">
        <Select
          v-model="dealershipsSelected"
          :options="dealershipsList"
          searchable
          mode="multiple"
          :chips="false"
          option-group-label="manufacturer"
          placeholder="Select a Dealership"
          option-group-children="dealerships"
          class="min-w-80 max-w-96"
        />

        <DatePicker
          v-model="dateRange"
          selection-mode="range"
          :close-on-select="false"
          show-presets
          class="min-w-60"
          :presets="dateRangePresets"
          :months-visible="2"
        />
        <Button
          class="flex-shrink-0"
          label="Update"
          :is-loading="customerDataApiRequest.isLoading.value"
          :disabled="!hasDealershipsSelected"
          @click="onDealershipChange"
        />
      </div>
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Appointed Customers"
      :value="customerData"
      removable-sort
      sort-mode="multiple"
      :sort-order="-1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['customerName', 'dealershipName', 'model', 'vrm', 'assignedToName', 'product', 'outcome']"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-report-appointed-customers"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :loading="customerDataApiRequest.isLoading.value"
      :export-function="formatExportData"
    >
      <template #empty>
        <Empty
          :title="hasDealershipsSelected ? 'No Appointments Found' : 'Select a dealership'"
          :icon="hasDealershipsSelected ? 'calendar-xmark' : 'building'"
        />
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <Column
        field="customerName"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="'/agreement/' + data.agreementId"
            class="text-primary link whitespace-nowrap"
          >
            {{ data.customerName }}
          </router-link>
        </template>
      </Column>

      <Column
        field="dealershipId"
        header="Dealership"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ data.dealershipName }}</div>
        </template>
      </Column>

      <Column
        field="assignedToName"
        header="Assigned To"
        :sortable="true"
      >
        <template #body="{ data }">
          <div
            v-if="data.assignedToName"
            class="whitespace-nowrap"
          >
            {{ data.assignedToName }}
          </div>
        </template>
      </Column>

      <Column
        field="time"
        header="Date"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link :to="'/agreement/' + data.agreementId + '/appointments/' + data.appointmentId">
            <div
              v-tippy="dayjs(data.time).format('H:mma DD/MM/YYYY')"
              class="text-primary whitespace-nowrap"
            >
              {{ data.time ? getFormattedDate(data.time, 'D MMM YYYY', { local: true }) : '' }}
            </div>
          </router-link>
        </template>
      </Column>

      <Column
        field="endDate"
        header="End Date"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">
            {{ data.endDate ? getFormattedDate(data.endDate, 'D MMM YYYY', { local: true }) : '' }}
          </div>
        </template>
      </Column>

      <Column
        field="term"
        header="Term"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ data.term }}</div>
        </template>
      </Column>

      <!-- Calculate months between appointment date and end date -->
      <Column
        field="appointedAt"
        :sortable="true"
      >
        <template #header>
          <div class="flex items-center gap-1">
            Remaining
            <Icon
              v-tippy="'Months remaining at time of appointment'"
              type="info-circle"
              fa-style="far"
            />
          </div>
        </template>
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ dayjs(data.endDate).diff(dayjs(data.appointedAt), 'months') }}</div>
        </template>
      </Column>

      <!-- Hidden so that the value is exported in CSV -->
      <Column
        field="status"
        header="Status"
        hidden
      >
        <template #body="{ data }">{{ data.status }}</template>
      </Column>

      <Column
        field="outcome"
        header="Outcome"
      >
        <template #body="{ data }">
          <div
            v-if="data.outcome"
            class="flex gap-1"
          >
            <Icon
              v-tippy="$t(getDictionaryItem('appointmentOutcome', data.outcome)?.localeKey)"
              :type="$t(getDictionaryItem('appointmentOutcome', data.outcome)?.icon)"
              :class="'text-' + getDictionaryItem('appointmentOutcome', data.outcome)?.color"
            />
            <div v-if="data.decision">
              {{ $t(getDictionaryItem('appointmentDecision', data.decision)?.localeKey) }}
            </div>

            <Icon
              v-if="data.outcomeNote"
              v-tippy="data.outcomeNote"
              type="note"
            />
          </div>
          <div
            v-else
            class="flex gap-1"
          >
            <div class="text-secondary whitespace-nowrap italic">No Outcome</div>
            <!-- <Icon v-if="data.status" v-tippy="data.status" type="info-circle" fa-style="far" /> -->
          </div>
        </template>
      </Column>

      <Column
        field="decision"
        header="Decision"
        hidden
      >
        <template #body="{ data }">
          <span v-if="data.decision">{{ $t(getDictionaryItem('appointmentDecision', data.decision)?.localeKey) }}</span>
        </template>
      </Column>

      <Column
        field="newUsed"
        header="New/Used"
      >
        <template #body="{ data }">
          {{ data.newUsed ? capitalize(data.newUsed) : '' }}
        </template>
      </Column>

      <Column
        field="productType"
        header="Product"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip>{{ data.productType }}</Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="productOptions"
            :chips="false"
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="max-w-[300px]"
          >
            <template #option="slotProps">
              {{ slotProps.option.value }}
            </template>
          </Select>
        </template>
      </Column>

      <!-- Hidden so that the value is exported in CSV -->
      <Column
        field="make"
        header="Make"
        hidden
      >
        <template #body="{ data }">
          {{ data.make }}
        </template>
      </Column>

      <Column
        field="model"
        header="Current Vehicle"
        filter-field="model"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="flex items-center">
            <Avatar
              :url="data.manufacturerLogo"
              is-logo
            />
            <div class="ml-3">
              <div class="font-semibold flex items-center whitespace-nowrap">
                {{ data.model }}

                <LicensePlate
                  :plate="data.vrm"
                  country="uk"
                  :design="data.fuelType == 'electric' ? 'ev' : ''"
                  greyscale
                  size="sm"
                />
              </div>
              <!-- <div
                class="text-md text-secondary whitespace-nowrap"
                style="margin-top: -3px"
              >
                {{ data.derivative }}
              </div> -->
            </div>
          </div>
        </template>
      </Column>

      <!-- Hidden so that the value is exported in CSV -->
      <Column
        field="derivative"
        header="Derivative"
        hidden
      >
        <template #body="{ data }">
          {{ data.derivative }}
        </template>
      </Column>

      <!-- <Column
        field="estimatedDeliveryDate"
        header="Delivery ETA"
        :sortable="true"
      >
        <template #body="{ data }">
          <div
            v-if="data.estimatedDeliveryDate"
            v-tippy="dayjs(data.estimatedDeliveryDate).format('DD/MM/YYYY')"
            class="whitespace-nowrap"
          >
            {{ dayjs().to(dayjs(data.estimatedDeliveryDate)) }}
          </div>
        </template>
      </Column> -->
    </DataTable>
  </Card>
</template>

<script setup>
import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Icon from '@/components/icon/Icon.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Chip from '@/components/chip/Chip.vue'
import Select from '@/components/forms/Select.vue'
import Empty from '@/components/empty/Empty.vue'

import LicensePlate from '@/components/license-plate/LicensePlate.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'

import dayjs from 'dayjs'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { getDictionaryItem } from '@/dictionary'

import { ref, onMounted, computed } from 'vue'
import groupDealershipsByManufacturer from '@/utilities/groupDealerships'
import { capitalize } from '@/utilities/string/capitalize'
import { getFormattedDate } from '@/utilities/dates'
const dealershipsList = ref([])

const authStore = useAuthStore()
onMounted(async () => {
  dealershipsList.value = await groupDealershipsByManufacturer(authStore.accessAllowed.dealerships)
})
const dt = ref()
const customerData = ref(null)

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])

const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

const productOptions = [
  { value: 'PCP', label: 'PCP' },
  { value: 'HP', label: 'HP' },
  { value: 'BCH', label: 'BCH' },
  { value: 'CH', label: 'CH' }
]

const dealershipsSelected = ref([])
const dealershipsSelectedLS = JSON.parse(localStorage.getItem('report-appointed-customers-dealerships-selected'))
if (dealershipsSelectedLS) {
  dealershipsSelected.value = dealershipsSelectedLS
}
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)

// If dealershipsSelected has values which aren't in dealershipsAllowed, remove them
dealershipsSelected.value = dealershipsSelected.value.filter(d => dealershipsAllowed.value.map(da => da.id).includes(d))

// If user only has access to 1 dealership and nothing is selected, auto-select it
if (dealershipsAllowed.value.length === 1 && dealershipsSelected.value.length === 0) {
  dealershipsSelected.value = [dealershipsAllowed.value[0].id]
}

localStorage.setItem('report-appointed-customers-dealerships-selected', JSON.stringify(dealershipsSelected.value))

const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    productType: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-report-appointed-customers')
  initFilters()
}

const customerDataApiRequest = useApiRequest()

const hasDealershipsSelected = computed(() => {
  return Array.isArray(dealershipsSelected.value) && dealershipsSelected.value.length > 0
})

function onDealershipChange() {
  localStorage.setItem('report-appointed-customers-dealerships-selected', JSON.stringify(dealershipsSelected.value))

  fetchCustomerData()
}

function fetchCustomerData() {
  // Only fetch if there's at least one dealership selected
  if (
    dateRange.value[0] &&
    dateRange.value[1] &&
    dealershipsSelected.value &&
    Array.isArray(dealershipsSelected.value) &&
    dealershipsSelected.value.length > 0
  ) {
    customerDataApiRequest
      .send({
        endpoint: '/v1/reports/appointments/appointed-customers',
        params: {
          start: dayjs(dateRange.value[0]).format('YYYY-MM-DD 00:00:00'),
          end: dayjs(dateRange.value[1]).format('YYYY-MM-DD 23:59:59'),
          dealerships: dealershipsSelected.value.join(',')
        }
      })
      .then(response => {
        if (response.success) {
          customerData.value = response.data
        }
      })
  }
}
if (dealershipsSelected.value.length) {
  fetchCustomerData()
}

function formatExportData({ data, field }) {
  switch (field) {
    case 'dealershipId':
      return dealershipsAllowed.value.find(d => d.id === data)?.name
    default:
      return data
  }
}

const exportCSV = () => {
  dt.value.exportCSV()
}
</script>

<style></style>
